import { useState, useRef, useEffect, FormEvent } from "react";
import { useTranslation } from "react-i18next";

function generateChallenge() {
  const num1 = Math.floor(Math.random() * 9) + 1;
  const num2 = Math.floor(Math.random() * 9) + 1;
  return { num1, num2, answer: num1 + num2 };
}

export default function ContactFormSection() {
  const { t, i18n } = useTranslation("home");
  const formRef = useRef<HTMLFormElement>(null);
  const [dsgvoChecked, setDsgvoChecked] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [challenge, setChallenge] = useState(generateChallenge);
  const [spamAnswer, setSpamAnswer] = useState("");
  const [spamError, setSpamError] = useState("");
  const maxChars = 500;

  // Cookie helpers for Google Maps consent
  const getCookie = (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  };
  const setCookie = (name: string, value: string, days: number) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
  };
  const [mapActivated, setMapActivated] = useState(false);
  useEffect(() => {
    if (getCookie("google-maps-consent") === "true") {
      setMapActivated(true);
    }
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // Honeypot check
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("phone_alt");
    if (honeypot && honeypot.toString().trim() !== "") {
      e.preventDefault();
      // Fake success – nicht abschicken
      form.reset();
      setCharCount(0);
      setDsgvoChecked(false);
      setSpamAnswer("");
      setSpamError("");
      setChallenge(generateChallenge());
      return;
    }

    // Spam challenge check
    if (!spamAnswer.trim()) {
      e.preventDefault();
      setSpamError(t("contactPage.form.spam_error"));
      return;
    }
    const userAnswer = parseInt(spamAnswer.trim(), 10);
    if (userAnswer !== challenge.answer) {
      e.preventDefault();
      setSpamError(t("contactPage.form.spam_wrong"));
      setSpamAnswer("");
      setChallenge(generateChallenge());
      return;
    }
    setSpamError("");

    // Valid – natives Form-Submit läuft durch, PHP handled den Rest
  };

  return (
    <section className="pt-6 pb-6 md:pt-8 md:pb-8 px-4 md:px-6 lg:px-8 bg-white">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 md:gap-16">
          {/* LEFT - Form */}
          <div className="flex-1">
            <h1 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {t("contactPage.form.title")}
            </h1>

            <form
              ref={formRef}
              action="/kontakt.php"
              method="POST"
              onSubmit={handleSubmit}
              acceptCharset="UTF-8"
              className="space-y-4"
            >
              {/* Hidden: Sprache */}
              <input type="hidden" name="lang" value={i18n.language} />

              {/* Hidden: Spam-Challenge Werte für PHP-Prüfung */}
              <input type="hidden" name="challenge_num1" value={challenge.num1} />
              <input type="hidden" name="challenge_num2" value={challenge.num2} />

              {/* Name */}
              <div>
                <label
                  htmlFor="contact-name"
                  className="block font-body text-sm font-medium text-foreground-700 mb-1"
                >
                  {t("contactPage.form.name")} *
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 font-body text-sm border border-background-300 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder={t("contactPage.form.name_placeholder")}
                />
              </div>

              {/* Company */}
              <div>
                <label
                  htmlFor="contact-company"
                  className="block font-body text-sm font-medium text-foreground-700 mb-1"
                >
                  {t("contactPage.form.company")}
                </label>
                <input
                  id="contact-company"
                  name="company"
                  type="text"
                  className="w-full px-4 py-2.5 font-body text-sm border border-background-300 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder={t("contactPage.form.company_placeholder")}
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="contact-email"
                  className="block font-body text-sm font-medium text-foreground-700 mb-1"
                >
                  {t("contactPage.form.email")} *
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 font-body text-sm border border-background-300 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder={t("contactPage.form.email_placeholder")}
                />
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="contact-phone"
                  className="block font-body text-sm font-medium text-foreground-700 mb-1"
                >
                  {t("contactPage.form.phone")}
                </label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="text"
                  className="w-full px-4 py-2.5 font-body text-sm border border-background-300 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder={t("contactPage.form.phone_placeholder")}
                />
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="contact-message"
                  className="block font-body text-sm font-medium text-foreground-700 mb-1"
                >
                  {t("contactPage.form.message")} *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={7}
                  maxLength={maxChars}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-2.5 font-body text-sm border border-background-300 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                  placeholder={t("contactPage.form.message_placeholder")}
                />
                <p className="font-body text-xs text-foreground-500 mt-1 text-right">
                  {charCount}/{maxChars}
                </p>
              </div>

              {/* Spam Challenge */}
              <div>
                <label
                  htmlFor="contact-spam"
                  className="block font-body text-sm font-medium text-foreground-700 mb-1"
                >
                  {t("contactPage.form.spam_question")} {challenge.num1} + {challenge.num2} ? *
                </label>
                <input
                  id="contact-spam"
                  name="spam_answer"
                  type="text"
                  inputMode="numeric"
                  value={spamAnswer}
                  onChange={(e) => {
                    setSpamAnswer(e.target.value);
                    if (spamError) setSpamError("");
                  }}
                  aria-invalid={spamError ? "true" : "false"}
                  aria-describedby={spamError ? "contact-spam-error" : undefined}
                  className="w-full px-4 py-2.5 font-body text-sm border border-background-300 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder="..."
                />
                {spamError && (
                  <p id="contact-spam-error" className="font-body text-xs text-red-600 mt-1" role="alert">{spamError}</p>
                )}
              </div>

              {/* Honeypot */}
              <div className="hp-field" aria-hidden="true">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="hp-input"
                />
              </div>

              {/* DSGVO Checkbox */}
              <div>
                <div className="flex items-start gap-2.5">
                  <input
                    id="contact-dsgvo"
                    name="dsgvo"
                    type="checkbox"
                    required
                    checked={dsgvoChecked}
                    onChange={(e) => setDsgvoChecked(e.target.checked)}
                    className="mt-0.5 w-4 h-4 flex-shrink-0 cursor-pointer accent-primary-500"
                  />
                  <label
                    htmlFor="contact-dsgvo"
                    className="font-body text-xs md:text-sm text-foreground-700 leading-relaxed cursor-pointer"
                  >
                    {t("contactPage.form.dsgvo_text")}
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="inline-flex items-center gap-2 px-8 py-2.5 font-heading text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("contactPage.form.submit")}
              </button>
            </form>
          </div>

          {/* RIGHT - Contact Details + Map */}
          <div className="flex-1">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {t("contactPage.details.title")}
            </h2>

            <div className="font-body text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              <div>
                <p className="font-bold text-foreground-950">
                  {t("contactPage.details.company")}
                </p>
                <p>{t("contactPage.details.address_line1")}</p>
                <p>{t("contactPage.details.address_line2")}</p>
                <p>{t("contactPage.details.address_country")}</p>
              </div>
              <div>
                <p>
                  {t("contactPage.details.tel_label")}{" "}
                  <a
                    href={`tel:${t("contactPage.details.tel_number").replace(/\s/g, "")}`}
                    className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {t("contactPage.details.tel_number")}
                  </a>
                </p>
                <p>
                  {t("contactPage.details.email_label")}{" "}
                  <a
                    href={`mailto:${t("contactPage.details.email_address")}`}
                    className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {t("contactPage.details.email_address")}
                  </a>
                </p>
              </div>
            </div>

            {/* Map */}
            <div className="mt-10 h-[280px] md:h-[320px] w-full">
              {mapActivated ? (
                <iframe
                  title={t("contactPage.map.title")}
                  src="https://www.google.com/maps?q=Heinrichstra%C3%9Fe+45,+40239+D%C3%BCsseldorf&z=16&output=embed"
                  className="w-full h-full border-0"
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-background-100 border border-background-300">
                  <div className="w-12 h-12 flex items-center justify-center mb-3 text-foreground-400">
                    <i className="ri-map-pin-line text-4xl" />
                  </div>
                  <p className="font-heading text-sm md:text-base font-semibold text-foreground-700 mb-2 text-center px-4">
                    {t("contactPage.map.preview_title")}
                  </p>
                  <p className="font-body text-xs md:text-sm text-foreground-500 mb-4 text-center max-w-sm px-4 leading-relaxed">
                    {t("contactPage.map.preview_text")}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setCookie("google-maps-consent", "true", 30);
                      setMapActivated(true);
                    }}
                    className="inline-flex items-center gap-2 px-6 py-2 font-heading text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-lock-unlock-line" />
                    {t("contactPage.map.activate_button")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}